// One TK-C charge, one ledger row — the shared rules (owner decision 2026-08-08).
//
// A draw can reach the ledger twice: the investor logs it by hand, then the
// 5-minute cron finds the same Taekus charge and creates it again. The cron
// dedupes on its own token and cannot see a hand-typed row. That is the whole
// of Kholy's 7,500 double-count (KHOLY_LEDGER_RECONSTRUCTION §5b): logged
// 15:45, drafted 16:20, confirmed, pool debited twice.
//
// Two consumers, deliberately sharing this module rather than each carrying a
// copy — a rule that exists twice is one that eventually disagrees with itself:
//
//   1. taekus-draw-core (server) ADOPTS: on meeting a token with no movement,
//      claim the hand-typed row instead of inserting a second one.
//   2. PendingDrafts (client) DISCLOSES: whatever adoption declined to decide
//      is shown to the confirmer, with the exact timestamp of what already
//      exists.
//
// Only the SERVER hands untrusted statuses in here — PendingDrafts filters to
// status='confirmed' in its own query. The denied-row rule below is therefore
// the adoption path's fix and the client's belt-and-braces.
//
// This module handles the human-vs-cron collision ONLY. The cron-vs-itself
// collision — one charge arriving twice as an authorisation and a completion —
// is not resemblance at all: Taekus names the pair via `previous_token`, and
// taekus-draw-core resolves it before adoption is ever consulted (T-030).
//
// We surface, we never merge on resemblance alone. Two identical transfers
// between the same accounts on the same day are ordinary here — Kholy buys
// 5,000 + 5,000 within a minute. See EMAIL_AGENT_AUDIT_AND_REDESIGN.md Part 4.
//
// Pure and dependency-free so it can be tested against real ledger rows.

export const COLLISION_WINDOW_MS = 12 * 60 * 60 * 1000; // ±12h (owner, 2026-08-08)

/** True when a movement already carries a Taekus token. */
export const isTokened = (m) =>
  typeof m?.source_ref === 'string' && m.source_ref.startsWith('TAEKUS-');

/**
 * The best timestamp a movement can offer, and what it actually means.
 *
 * Only taekus_draw rows carry movement_time — every hand-logged row has it
 * NULL — so for those the honest answer is when it was LOGGED, not when the
 * transaction happened. Callers must say which rather than implying a
 * transaction time we do not have.
 */
export function movementStamp(m) {
  if (m?.movement_time) return { at: new Date(m.movement_time), kind: 'transacted' };
  if (m?.created_at) return { at: new Date(m.created_at), kind: 'logged' };
  return null;
}

const validAt = (m) => {
  const at = movementStamp(m)?.at;
  return at && !Number.isNaN(at.getTime()) ? at : null;
};

/**
 * Could this existing row BE the same charge as `draw`?
 *
 * Anchored on the SOURCE account (the member's TK-C card) + amount + time.
 * Destination deliberately does not gate the match: the cron's destination is
 * only a merchant-pattern *suggestion*, and a human who logged the same charge
 * may well have picked a different one. Requiring destination equality would
 * silently miss exactly the duplicates we are hunting.
 *
 * A row that already carries a token is never a candidate — it is already some
 * Taekus record's representation, so claiming it again would attach a second
 * record to one row. (This is NOT the same as "a tokened neighbour is a
 * different real charge": a token names a RECORD, not a charge, and one charge
 * can carry two — see T-030. Khaled's two 1,316.25 tokens seconds apart turned
 * out to be one Wise charge's authorisation and its completion, not two
 * charges. The pairing of those two records is `previous_token`'s job in
 * taekus-draw-core, never this module's.)
 *
 * A DENIED row is never a candidate either. A human retired it, and adoption
 * deliberately does not change status — so claiming one would spend the charge's
 * token on a row that is not in the ledger, and no pending draw would ever be
 * created for a charge Taekus says is real. That is a decision about a
 * hand-logged row being silently applied to the charge itself, which are not the
 * same decision. Seen live: Kholy's `e6d3e75c` was retired 2026-08-08, then
 * adopted at 02:50 the next day — its notes carry both stamps, in that order.
 * `pending` stays adoptable: nobody has decided yet, and attaching the evidence
 * before they do is the whole point.
 */
export function isCandidate(draw, c, windowMs = COLLISION_WINDOW_MS) {
  if (!c || !draw || c.id === draw.id) return false;
  if (c.source_account_id !== draw.source_account_id) return false;
  if (!Number.isFinite(Number(c.amount)) || !Number.isFinite(Number(draw.amount))) return false;
  if (Math.abs(Number(c.amount) - Number(draw.amount)) > 0.005) return false;
  if ((c.status || 'confirmed') === 'denied') return false;
  if (isTokened(c)) return false;
  const at = validAt(c), drawAt = validAt(draw);
  return !!at && !!drawAt && Math.abs(at.getTime() - drawAt.getTime()) <= windowMs;
}

/** Candidates, with same-destination ones first — the likeliest same charge. */
export function candidatesFor(draw, rows, windowMs = COLLISION_WINDOW_MS) {
  const hits = (rows || []).filter((c) => isCandidate(draw, c, windowMs));
  return hits.sort((a, b) =>
    (b.dest_account_id === draw.dest_account_id) - (a.dest_account_id === draw.dest_account_id));
}

/**
 * An authorisation and its completion share one txn_at exactly (T-030), so two
 * cron rows this close are one charge, not two.
 */
export const SAME_CHARGE_MS = 60 * 1000;

/**
 * Is this candidate ALREADY the accepted counterpart of some OTHER charge?
 *
 * `isCandidate` asks whether a row is free — no token, not denied. That is not
 * the same question as whether it is unclaimed. A hand-logged row can already be
 * doing a job without ever carrying a token: when the cron records a charge and
 * a human denies that cron row as the duplicate of the hand-logged one, the
 * denial IS the statement "this hand-logged row is that charge". It leaves no
 * mark on the hand-logged row at all — only on the retired one beside it.
 *
 * Adoption used to be blind to that, and a later charge of the same amount would
 * claim the row out from under the earlier one. Salman, 2026-08-10/11, cost
 * 3,000 (SALMAN_LEDGER_RECONSTRUCTION.md §9b):
 *
 *   Aug 10 17:16  charge -> cron row c83b8756, then hand-logged as a13143a7
 *                 -> human denies c83b8756. a13143a7 now carries Aug 10.
 *   Aug 11 02:34  a DIFFERENT charge, same card, same 3,000, adopts a13143a7 —
 *                 same amount, same account, within a day, no token. The Aug 10
 *                 charge is left with nothing but a denied row.
 *
 * So: a retired CRON row (denied and tokened — it named a real Taekus record)
 * sitting at the same account and amount, near the candidate in time, means the
 * candidate is spoken for. The incoming draw must insert instead.
 *
 * The exception that must not be broken: when the denied row is the SAME charge
 * as the incoming draw, it is not evidence of another one — the human denied the
 * cron's copy precisely so the hand-logged row would represent this charge, and
 * attaching the token to it is right. Their transacted stamps identify that case.
 */
export function isSpokenFor(c, draw, rows, windowMs = COLLISION_WINDOW_MS) {
  const cAt = validAt(c);
  if (!cAt) return false;
  const drawAt = validAt(draw);
  return (rows || []).some((r) => {
    if (!r || r.id === c.id || r.id === draw.id) return false;
    if ((r.status || 'confirmed') !== 'denied') return false;
    if (!isTokened(r)) return false;
    if (r.source_account_id !== c.source_account_id) return false;
    if (!Number.isFinite(Number(r.amount))) return false;
    if (Math.abs(Number(r.amount) - Number(c.amount)) > 0.005) return false;
    const rAt = validAt(r);
    if (!rAt) return false;
    // Same charge as the incoming draw → not evidence of a second one.
    if (drawAt && Math.abs(rAt.getTime() - drawAt.getTime()) <= SAME_CHARGE_MS) return false;
    return Math.abs(rAt.getTime() - cAt.getTime()) <= windowMs;
  });
}

/**
 * DISCLOSURE (client): the row worth telling the confirmer about, or null.
 * Ambiguity is fine here — showing the closest match is still better than
 * showing nothing.
 */
export function findCollision(draw, rows, windowMs = COLLISION_WINDOW_MS) {
  return candidatesFor(draw, rows, windowMs)[0] || null;
}

/**
 * ADOPTION (server): may the cron claim an existing row instead of inserting?
 *
 * Only when the answer is unambiguous. Preference order:
 *   1. exactly one candidate sharing the destination  → adopt it
 *   2. no destination match but exactly one candidate → adopt it
 *   3. anything else                                  → decline, and let the
 *      confirm screen disclose it to a human
 *
 * Rule 1 before rule 2 matters: two same-amount draws on one day to different
 * merchants must not have the first token claim the wrong row just because it
 * was processed first.
 *
 * Candidates that are already spoken for by another charge are removed first —
 * see isSpokenFor. If that leaves nothing, this reports `ambiguous` rather than
 * null, because "a row exists but is not mine to take" is precisely what the
 * confirmer needs told; returning null would insert the draw with no disclosure.
 *
 * @returns {{movement: object}|{ambiguous: true, count: number, spokenFor?: true}|null}
 */
export function findAdoptable(draw, rows, windowMs = COLLISION_WINDOW_MS) {
  const all = candidatesFor(draw, rows, windowMs);
  if (!all.length) return null;
  const hits = all.filter((c) => !isSpokenFor(c, draw, rows, windowMs));
  if (!hits.length) return { ambiguous: true, count: all.length, spokenFor: true };
  const sameDest = hits.filter((c) => c.dest_account_id === draw.dest_account_id);
  if (sameDest.length === 1) return { movement: sameDest[0] };
  if (sameDest.length > 1) return { ambiguous: true, count: sameDest.length };
  if (hits.length === 1) return { movement: hits[0] };
  return { ambiguous: true, count: hits.length };
}
